/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useMemo } from "react";

import { CheckCircle } from "lucide-react";

import { cn } from "@/lib/utils";
import { FadeIn } from "./fade-in";

export interface FeatureItem {
  /** Icon component */
  icon: React.ComponentType<{ className?: string }>;
  /** Feature title */
  title: string;
  /** Feature description */
  description: string;
  /** Optional list of benefits */
  benefits?: string[];
  /** Optional additional details text */
  details?: string;
}

export interface FeatureGridProps {
  /** Array of feature items */
  features: FeatureItem[];
  /** Number of columns (default: 3) */
  columns?: 1 | 2 | 3 | 4;
  /** Additional className */
  className?: string;
}

export function FeatureGrid({
  features,
  columns = 3,
  className = "",
}: FeatureGridProps) {
  const gridCols = useMemo(() => {
    const colsMap: Record<typeof columns, string> = {
      1: "grid-cols-1",
      2: "md:grid-cols-2",
      3: "md:grid-cols-2 lg:grid-cols-3",
      4: "md:grid-cols-2 lg:grid-cols-4",
    };
    return colsMap[columns];
  }, [columns]);

  return (
    <div className={cn("grid gap-6", gridCols, className)}>
      {features.map((feature, index) => {
        const IconComponent = feature.icon;
        return (
          <FadeIn
            key={index}
            delay={index * 100}
            className="card-premium rounded-xl border border-muted-foreground/20 bg-card p-4 md:p-5 shadow-sm"
          >
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-muted to-secondary">
              <IconComponent className="h-5 w-5 text-secondary-foreground" />
            </div>

            <h3 className="mb-4 text-base font-semibold text-foreground">
              {feature.title}
            </h3>

            <p className="mb-4 text-muted-foreground">{feature.description}</p>

            {feature.benefits && feature.benefits.length > 0 && (
              <div className="mb-4">
                <ul className="space-y-2">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <li
                      key={benefitIndex}
                      className="flex items-center text-sm text-muted-foreground"
                    >
                      <CheckCircle className="mr-2 h-4 w-4 text-success" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {feature.details && (
              <p className="text-sm leading-relaxed text-muted-foreground">
                {feature.details}
              </p>
            )}
          </FadeIn>
        );
      })}
    </div>
  );
}
